import React from "react";
import { CalendarClock } from "lucide-react";
import { COLOR } from "./pillars/_cards";
import { avgDailyMiles } from "../../lib/batteryAnalytics";
import { warrantyFor } from "../../lib/brand";

/** Warranty Claim Forecaster — answers the one question other apps don't:
 *  "Will my pack cross the warranty floor BEFORE coverage ends?" */
export const WarrantyForecastCard = ({ vehicle }) => {
  const soh = vehicle?.sohCurrent ?? null;
  if (soh == null) return null;

  const floor = warrantyFor(vehicle).threshold;
  const years = warrantyFor(vehicle).years;
  const milesCap = warrantyFor(vehicle).miles;
  const velocity = Math.max(0.05, Math.abs(vehicle?.delta30d ?? 0.2)); // %/mo

  const start = vehicle?.inServiceDate
    ? new Date(vehicle.inServiceDate)
    : new Date(vehicle?.year || new Date().getFullYear() - 3, 5, 1);
  const end = new Date(start); end.setFullYear(start.getFullYear() + years);
  const monthsLeftTime = Math.max(0, (end.getTime() - Date.now()) / (30.44 * 86400e3));
  const dailyMi = avgDailyMiles(vehicle);
  const milesLeft = Math.max(0, milesCap - (vehicle?.odometer ?? 0));
  const monthsLeftMiles = dailyMi > 0 ? milesLeft / (dailyMi * 30.44) : monthsLeftTime;
  const monthsLeft = Math.min(monthsLeftTime, monthsLeftMiles);
  const endsByMiles = monthsLeftMiles < monthsLeftTime;

  const projected = +(soh - velocity * monthsLeft).toFixed(1);
  const margin = +(projected - floor).toFixed(1);
  const replacementEst = Math.round(((vehicle?.packKwh || 75) * 140 + 2500) / 500) * 500;

  const verdict =
    margin >= 3
      ? { label: "Unlikely to qualify", color: COLOR.green,
          tip: "Your pack is outpacing the warranty floor — that's the good outcome. Nothing to file." }
      : margin >= -1
      ? { label: "Borderline — monitor", color: COLOR.amber,
          tip: "Projection lands within a point of the floor. Watch monthly — a diagnostic before coverage ends protects a claim." }
      : { label: "Trending toward a claim", color: COLOR.red,
          tip: `On this trajectory the pack crosses ${floor}% while covered — a free replacement is worth ~$${replacementEst.toLocaleString()}. Use the claim drafter above.` };

  const endLabel = monthsLeft <= 0
    ? "Coverage ended"
    : endsByMiles
      ? `~${Math.round(monthsLeft)} mo (hits ${(milesCap / 1000).toFixed(0)}k mi first)`
      : end.toLocaleDateString(undefined, { month: "short", year: "numeric" });

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
      data-testid="warranty-forecast-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
          style={{ background: "rgba(0,229,255,0.10)", borderColor: "rgba(0,229,255,0.35)" }}
        >
          <CalendarClock className="w-4 h-4" style={{ color: COLOR.cyan }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Claim forecaster
          </div>
          <div className="text-sm text-white font-semibold leading-snug">
            Where your pack lands when coverage ends
          </div>
        </div>
        <span
          className="px-3 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-wide shrink-0"
          style={{ color: verdict.color, background: `${verdict.color}18`, border: `1px solid ${verdict.color}55` }}
          data-testid="warranty-forecast-verdict"
        >
          {verdict.label}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-white/5">
        <div>
          <div className="font-display text-xl text-white font-semibold leading-none" data-testid="warranty-forecast-projected">
            {projected}<span className="font-mono text-[10px] text-zinc-400">%</span>
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">projected SoH</div>
        </div>
        <div>
          <div className="font-display text-xl font-semibold leading-none" style={{ color: verdict.color }}>
            {margin > 0 ? "+" : ""}{margin}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">vs {floor}% floor</div>
        </div>
        <div>
          <div className="font-display text-[15px] text-white font-semibold leading-tight pt-0.5">{endLabel}</div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">coverage ends</div>
        </div>
      </div>

      <p className="font-mono text-[10px] text-zinc-400 leading-relaxed mt-3" data-testid="warranty-forecast-tip">
        {verdict.tip}
      </p>
    </div>
  );
};
